import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import { FaMinus, FaPlus } from 'react-icons/fa';
import { AiComponentHtmlAttribute, AiManifestComponent } from '@/pkg/ai';
import { AiComponentGenericInput } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/generic-input';
import { Tooltip } from '@/components/tooltip';
import { useAiComponentContext } from '@/features/editor/widgets/shared/ai-editing-layout/context';
import { EditorContext } from '@/features/editor/context/editor-context';
import { DeviceType } from '@/utils/definitions';
import { toGroupName } from '@/features/editor/widgets/shared/ai-editing-layout/utils/group-name';

export interface AiComponentInputCardProps {
  component: AiManifestComponent;
}

export function AiComponentInputCard({ component }: AiComponentInputCardProps) {
  const {
    devicePreview: {
      editorState: { device },
    },
  } = useContext(EditorContext);
  const [isExpanded, setIsExpanded] = React.useState(true);
  const getAttribute = useAiComponentContext((ctx) => ctx.getAttribute);
  const setAttribute = useAiComponentContext((ctx) => ctx.setAttribute);
  const isFirstInGroup = useAiComponentContext((ctx) => ctx.isFirstInGroup);
  const toggleExpand = () => {
    if (!isRemoved) {
      setIsExpanded((prev) => !prev);
    }
  };

  const canBeRemoved = useMemo(
    () => component.type === `html-content`,
    [component],
  );

  const mockDisplayComponent = useMemo(
    () =>
      ({
        attr: `hidden`,
        type: `html-attr`,
        node: ``,
        value: ``,
        name: ``,
        description: ``,
        selector: component.selector,
      } as AiComponentHtmlAttribute),
    [component],
  );

  const [isRemoved, setIsRemoved] = React.useState(
    canBeRemoved &&
      getAttribute(component.selector, mockDisplayComponent) === `true`,
  );

  const onToggleVisibility = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (canBeRemoved) {
      setIsRemoved((prev) => !prev);
      setIsExpanded(isRemoved);
      setAttribute(
        component.selector,
        mockDisplayComponent,
        isRemoved ? `` : `true`,
      );
    }
  };

  const groupClassName = useMemo(
    () => toGroupName(component.group),
    [component],
  );

  return (
    <>
      {isFirstInGroup(component) && (
        <GroupName className={`vsly-group-${groupClassName}`}>
          {component.group}
        </GroupName>
      )}
      <Wrapper className={device === DeviceType.Desktop ? `desktop` : ``}>
        <TitleWrapper onClick={toggleExpand}>
          <Title
            className={`vsly-comp-selector-${toGroupName(component?.selector)}`}
          >
            {component.name}
            {component.description && (
              <Tooltip title={component.name} text={component.description} />
            )}
          </Title>
          <ControlsWrapper>
            {canBeRemoved && (
              <DisableButton onClick={onToggleVisibility}>
                {isRemoved ? `Enable` : `Disable`}
              </DisableButton>
            )}
            {isExpanded && <FaMinus />}
            {!isExpanded && <FaPlus />}
          </ControlsWrapper>
        </TitleWrapper>
        {isExpanded && (
          <ContentWrapper>
            <AiComponentGenericInput component={component} />
          </ContentWrapper>
        )}
      </Wrapper>
    </>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  background-color: transparent;
  border: solid 1px #dee1e6;
  border-radius: 2rem;
  padding: 2rem;

  &&:has(.ai-component-generic-input:empty) {
    display: none;
  }

  &&.desktop {
    padding: 1.5rem;

    div[class*='custom-toolbar-rich-text'] {
      gap: 0.5rem;
    }
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  color: #7c8c9a;
  cursor: pointer;

  &:hover {
    opacity: 0.6;
  }

  &:active {
    opacity: 1;
  }

  transition: opacity 0.2s ease-in-out;

  user-select: none;
  * {
    user-select: none;
  }
`;

const Title = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  font-weight: bold;
  font-size: 1.6rem;
`;

const GroupName = styled(Title)`
  && {
    color: #555555;
    &&:not(:first-child) {
      margin-top: 4rem;
    }

    transition: color 0.3s ease-in-out;
  }
`;

const ControlsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 1rem;
`;

const DisableButton = styled.button`
  background: white;
  text-transform: uppercase;
  user-select: none;
  cursor: pointer;
  appearance: none;
  color: rgb(166, 165, 165);
  border-radius: 2rem;
  padding: 0.3rem 0.8rem;
  font-family: 'JetBrains Mono', serif;
  font-size: 1rem;
  font-weight: 600;
  border: 1px solid rgba(213, 217, 220, 0.34);
  box-shadow: rgba(0, 0, 0, 0.15) 0px 1px 1px 0px,
    rgba(151, 210, 206, 0.22) 0px 6px 7px 0px;

  &:hover {
    filter: brightness(80%);
  }
`;

const ContentWrapper = styled.div``;
